import React, { useEffect, useMemo, useState } from 'react';
import { Database, LoaderCircle, LockKeyhole, Settings2 } from 'lucide-react';
import { fetchFeaturesApi } from '../../api/features.api';
import { Button } from '../../components/ui/Button';
import { Modal, MODAL_BODY_CLASS } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { useAppDispatch } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { updateProjectFeatures } from '../../redux/slices/projectsSlice';
import { classNames } from '../../utils/classNames';
import { getNewlyEnabledFeatures } from '../../utils/featureActivationPrompts';

const FEATURE_ICONS = {
  auth: LockKeyhole,
  database: Database
};

const MODAL_BODY_BG = MODAL_BODY_CLASS;

const getFeatureCardClass = (key, enabled) => {
  if (key === 'auth') {
    return enabled
      ? `border-sky-300/55 ${MODAL_BODY_BG}`
      : `border-sky-200/70 ${MODAL_BODY_BG}`;
  }

  if (key === 'database') {
    return enabled
      ? `border-emerald-300/55 ${MODAL_BODY_BG}`
      : `border-emerald-200/70 ${MODAL_BODY_BG}`;
  }

  return enabled ? `border-accent/35 ${MODAL_BODY_BG}` : `border-border/80 ${MODAL_BODY_BG}`;
};

const getFeatureIconClass = (key, enabled) => {
  if (key === 'auth') {
    return enabled
      ? `border-sky-300/45 ${MODAL_BODY_BG} text-sky-700 dark:text-sky-300`
      : `border-sky-200/60 ${MODAL_BODY_BG} text-sky-600 dark:text-sky-400`;
  }

  if (key === 'database') {
    return enabled
      ? `border-emerald-300/45 ${MODAL_BODY_BG} text-emerald-700 dark:text-emerald-300`
      : `border-emerald-200/60 ${MODAL_BODY_BG} text-emerald-600 dark:text-emerald-400`;
  }

  return enabled
    ? `border-accent/25 ${MODAL_BODY_BG} text-accent`
    : `border-border/70 ${MODAL_BODY_BG} text-accent`;
};

const innerSurfaceClass = `rounded-lg border border-border/70 ${MODAL_BODY_BG}`;

const getFeatureKey = (feature) => feature?.key || feature?.Key || '';

const normalizeProjectFeatureState = (features = []) =>
  features.reduce((accumulator, feature) => {
    const key = getFeatureKey(feature);

    if (key) {
      accumulator[key] = {
        enabled: true,
        metadata: feature?.metadata || feature?.Metadata || {}
      };
    }

    return accumulator;
  }, {});

const buildDraftFromProject = (project) => normalizeProjectFeatureState(project?.features || []);

const buildActivationDraft = (project, focusFeature) => {
  const draft = buildDraftFromProject(project);

  if (!focusFeature) {
    return draft;
  }

  draft[focusFeature] = {
    enabled: true,
    metadata:
      focusFeature === 'auth'
        ? {
            registrationEnabled: draft.auth?.metadata?.registrationEnabled ?? true
          }
        : draft[focusFeature]?.metadata || {}
  };

  return draft;
};

const mergeCatalogWithProjectFeatures = (catalog, projectFeatures) => {
  const byKey = new Map();

  catalog.forEach((feature) => {
    if (feature.key) {
      byKey.set(feature.key, feature);
    }
  });

  projectFeatures.forEach((feature) => {
    const key = getFeatureKey(feature);

    if (key && !byKey.has(key)) {
      byKey.set(key, {
        key,
        title: feature.title || feature.Title || key,
        description: feature.description || feature.Description || null,
        metadata: feature.metadata || feature.Metadata || {}
      });
    }
  });

  return Array.from(byKey.values()).filter((feature) => feature.key === 'auth' || feature.key === 'database');
};

const getModalTitle = (mode, focusFeature, t) => {
  if (mode !== 'activate' || !focusFeature) {
    return t('featuresSettingsTitle');
  }

  if (focusFeature === 'auth') {
    return t('featuresActivateAuthTitle');
  }

  if (focusFeature === 'database') {
    return t('featuresActivateDatabaseTitle');
  }

  return t('featuresSettingsTitle');
};

const getModalBody = (mode, focusFeature, t) => {
  if (mode !== 'activate' || !focusFeature) {
    return t('featuresSettingsBody');
  }

  if (focusFeature === 'auth') {
    return t('featuresActivateAuthBody');
  }

  if (focusFeature === 'database') {
    return t('featuresActivateDatabaseBody');
  }

  return t('featuresSettingsBody');
};

export const ProjectFeaturesModal = ({
  open,
  onClose,
  project,
  mode = 'full',
  focusFeature = null,
  onActivated = null
}) => {
  const { t } = useI18n();
  const dispatch = useAppDispatch();
  const isActivateMode = mode === 'activate' && Boolean(focusFeature);
  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState(() => buildDraftFromProject(project));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(
        isActivateMode ? buildActivationDraft(project, focusFeature) : buildDraftFromProject(project)
      );
      setError('');
      setSuccess('');
    }
  }, [focusFeature, isActivateMode, open, project]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    const loadCatalog = async () => {
      setLoading(true);
      setError('');

      try {
        const items = await fetchFeaturesApi();

        if (!cancelled) {
          setCatalog(items);
        }
      } catch (loadError) {
        if (!cancelled) {
          setCatalog([]);
          setError(loadError?.message || t('featuresLoadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const features = useMemo(() => {
    const merged = mergeCatalogWithProjectFeatures(catalog, project?.features || []);

    if (!isActivateMode) {
      return merged;
    }

    return merged.filter((feature) => feature.key === focusFeature);
  }, [catalog, focusFeature, isActivateMode, project?.features]);

  const toggleFeature = (key, enabled) => {
    setDraft((current) => ({
      ...current,
      [key]: {
        enabled,
        metadata: current[key]?.metadata || {}
      }
    }));
    setSuccess('');
  };

  const toggleRegistration = (enabled) => {
    setDraft((current) => ({
      ...current,
      auth: {
        enabled: current.auth?.enabled ?? true,
        metadata: {
          ...(current.auth?.metadata || {}),
          registrationEnabled: enabled
        }
      }
    }));
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!project?.id || saving) {
      return;
    }

    const previousFeatures = project?.features || [];
    const payloadFeatures = Object.entries(draft)
      .filter(([, value]) => value.enabled)
      .map(([key, value]) => {
        if (key === 'auth') {
          return {
            key,
            metadata: {
              registrationEnabled: Boolean(value.metadata?.registrationEnabled)
            }
          };
        }

        return { key };
      });

    setSaving(true);
    setError('');
    setSuccess('');

    const action = await dispatch(updateProjectFeatures({ projectId: project.id, features: payloadFeatures }));

    setSaving(false);

    if (updateProjectFeatures.fulfilled.match(action)) {
      const updatedProject = action.payload;
      const newlyEnabled = getNewlyEnabledFeatures(previousFeatures, updatedProject.features || []);

      if (newlyEnabled.length > 0 && onActivated) {
        onActivated({
          newlyEnabled,
          updatedProject,
          draft
        });
      }

      if (isActivateMode) {
        onClose();
        return;
      }

      setSuccess(t('featuresSaveSuccess'));
      return;
    }

    setError(action.error?.message || t('featuresSaveError'));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={getModalTitle(mode, focusFeature, t)}
      closeDisabled={saving}
      panelClassName="flex max-h-[min(90vh,720px)] max-w-2xl flex-col overflow-hidden p-0"
      contentClassName="mb-0 shrink-0 gap-2 border-b border-border/70 px-2 py-2"
      titleClassName="text-start text-base"
      closeButtonClassName="h-8 w-8 rounded-full p-0 text-lg"
    >
      <form className="flex min-h-0 flex-1 flex-col text-start" onSubmit={handleSubmit}>
        <div className="mx-2 mb-2 min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg px-3 py-3">
          {isActivateMode && (focusFeature === 'auth' || focusFeature === 'database') ? (
            <div className={classNames(innerSurfaceClass, 'border-accent/25 px-2.5 py-2')}>
              <p className="text-sm leading-6 text-muted">{getModalBody(mode, focusFeature, t)}</p>
            </div>
          ) : (
            <div className={classNames(innerSurfaceClass, 'px-2.5 py-2')}>
              <p className="text-xs leading-6 text-muted">{getModalBody(mode, focusFeature, t)}</p>
            </div>
          )}

          {loading ? (
            <div className={classNames(innerSurfaceClass, 'flex min-h-[100px] items-center justify-center gap-2 text-sm text-muted')}>
              <LoaderCircle className="animate-spin text-accent" size={18} />
              {t('featuresLoading')}
            </div>
          ) : (
            <div className="space-y-2">
              {features.map((feature) => {
                const Icon = FEATURE_ICONS[feature.key] || Settings2;
                const enabled = Boolean(draft[feature.key]?.enabled);

                return (
                  <section
                    key={feature.key}
                    className={classNames('rounded-lg border px-2.5 py-2 transition', getFeatureCardClass(feature.key, enabled))}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span
                          className={classNames(
                            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                            getFeatureIconClass(feature.key, enabled)
                          )}
                        >
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-text">{feature.title || t(`feature_${feature.key}`)}</h3>
                          <p className="mt-0.5 text-xs leading-5 text-muted">
                            {feature.description || t(`feature_${feature.key}_hint`)}
                          </p>
                        </div>
                      </div>
                      {!isActivateMode ? (
                        <Switch
                          checked={enabled}
                          disabled={saving}
                          onChange={(checked) => toggleFeature(feature.key, checked)}
                          aria-label={feature.title || feature.key}
                        />
                      ) : null}
                    </div>

                    {feature.key === 'auth' && enabled ? (
                      <div className={classNames(innerSurfaceClass, 'mt-2 border-sky-200/70 px-2.5 py-2 dark:border-sky-800/50')}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-text">{t('featuresRegistrationEnabled')}</p>
                            <p className="mt-0.5 text-xs text-muted">{t('featuresRegistrationEnabledHint')}</p>
                          </div>
                          <Switch
                            checked={Boolean(draft.auth?.metadata?.registrationEnabled)}
                            disabled={saving}
                            onChange={toggleRegistration}
                            aria-label={t('featuresRegistrationEnabled')}
                          />
                        </div>
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}

          {error ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              {success}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border/70 px-2 py-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            {t('close')}
          </Button>
          <Button type="submit" disabled={saving || loading}>
            {saving ? t('featuresSaving') : isActivateMode ? t('featuresActivate') : t('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
